import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { PageWrapper } from "../../../../shared/components/layout/PageWrapper";
import { ContextInfoCard } from "../../../../shared/components/ui/ContextInfoCard";
import { Card } from "../../../../shared/components/ui/Card";
import { Button } from "../../../../shared/components/ui/Button";
import { ConfirmationModal } from "../../../../shared/components/ui/ConfirmationModal";
import { ROUTES } from "../../../../routes/routes";
import { RoleEnum } from "../../../../api/client/types/user.types";
import { useAuthStore } from "../../../../store";
import { useDeleteSwimmerPage } from "./useDeleteSwimmerPage";

export const DeleteSwimmersPage: React.FC = () => {
  const { swimmerId } = useParams<{ swimmerId: string }>();
  const { user } = useAuthStore();
  const {
    swimmer,
    isLoadingSwimmer,
    isErrorSwimmer,
    isDeleting,
    handleConfirmDelete,
    handleNavigateToDashboard,
  } = useDeleteSwimmerPage();

  const [isModalOpen, setIsModalOpen] = useState(false);

  // Ensure always at top on open
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [swimmerId]);

  const contextItems = useMemo(() => {
    if (!swimmer) return [];
    return [
      { label: "Salvavidas de turno", value: swimmer.lifeguard_name },
      { label: "Unidad residencial", value: swimmer.building_name },
      { label: "Ciudad", value: swimmer.city },
      {
        label: "Clasificación de edad",
        value: swimmer.age_classification?.name,
      },
      { label: "Responsable", value: swimmer.responsible?.name },
    ];
  }, [swimmer]);

  if (!swimmerId) {
    const isLifeguard = user?.role === RoleEnum.LIFEGUARD;
    const dashboard = isLifeguard
      ? ROUTES.DASHBOARD.LIFEGUARD
      : ROUTES.DASHBOARD.ADMINISTRATOR;
    return <Navigate to={dashboard} replace />;
  }

  const hasDependents = (swimmer?.dependents?.length ?? 0) > 0;

  return (
    <PageWrapper
      isLoading={isLoadingSwimmer || isDeleting}
      loadingLabel={isDeleting ? "Eliminando bañista..." : "Cargando bañista..."}
      isError={isErrorSwimmer}
      errorMessage="No se pudo cargar el bañista. Puede que no exista o no tenga permiso para eliminarlo."
      onBack={handleNavigateToDashboard}
      title="Eliminar Bañista"
    >
      {swimmer && (
        <div className="flex flex-col gap-6 w-full max-w-4xl mx-auto">
          <ContextInfoCard title="Contexto de bañista" items={contextItems} />

          <Card>
            <h2 className="text-lg font-semibold text-gray-900 mb-2">
              Confirmar eliminación
            </h2>
            <p className="text-sm text-gray-700 mb-4">
              Estás a punto de eliminar al bañista{" "}
              <span className="font-semibold">{swimmer.name}</span>. Esta acción
              no se puede deshacer.
            </p>

            {hasDependents && (
              <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-3">
                <p className="text-sm font-semibold text-amber-800">
                  Atención: este bañista es responsable de los siguientes
                  menores de 12 años. Al eliminarlo, también se eliminarán estos
                  registros:
                </p>
                <ul className="mt-2 list-disc list-inside text-sm text-amber-900">
                  {swimmer.dependents.map((d) => (
                    <li key={d.id}>
                      <span className="font-medium">{d.name}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {!hasDependents && (
              <p className="text-sm text-gray-700 mb-4">
                Este bañista no tiene dependientes asociados.
              </p>
            )}

            <div className="flex justify-end gap-3 mt-4">
              <Button
                type="button"
                variant="primary"
                onClick={() => setIsModalOpen(true)}
                disabled={isDeleting}
                className="text-sm px-4 py-2 bg-red-600 hover:bg-red-700 border-red-600 hover:border-red-700"
              >
                Eliminar bañista
              </Button>
            </div>
          </Card>
        </div>
      )}

      {swimmer && (
        <ConfirmationModal
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onConfirm={() => {
            handleConfirmDelete();
            setIsModalOpen(false);
          }}
          title="Eliminar bañista"
          message={
            hasDependents
              ? `¿Está seguro que desea eliminar al bañista "${swimmer.name}"? Esta acción no se puede deshacer. También se eliminarán ${swimmer.dependents.length} dependiente(s) asociados.`
              : `¿Está seguro que desea eliminar al bañista "${swimmer.name}"? Esta acción no se puede deshacer.`
          }
          confirmText="Eliminar"
          cancelText="Cancelar"
          isLoading={isDeleting}
          variant="danger"
        />
      )}
    </PageWrapper>
  );
};

